/**
 * ProductGallery Block — Token-Aware + Variant Support
 *
 * DATA PRIORITY:
 *   1. props.data.images  (JSON-provided image array)
 *   2. context.product.media_gallery / image (SSR-injected Magento data)
 *
 * VARIANT SUPPORT:
 *   Accepts a `variant` prop (e.g. 'bento-dark') and applies conditional CSS.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useProduct } from '@agentic/magento-client/lib/hooks/useProduct';
import { BadgeOverlays } from '../../labels/ProductBadge';
import { getOptimizedImageUrl, webpFallback } from '../../../utils/image';

interface GalleryImage { url: string; label?: string }
interface ProductGalleryData {
  glowIntensity?: number;
  thumbnailPosition?: string;
  zoomMode?: string;
  showThumbnails?: boolean;
  images?: GalleryImage[];
  variant?: string;
  content?: {
    variant?: string;
    thumbnailPosition?: string;
    zoomMode?: string;
    showThumbnails?: boolean;
  };
  settings?: {
    glowIntensity?: number;
    thumbnailPosition?: string;
    zoomMode?: string;
  };
}
interface ProductGalleryProps {
  data?: ProductGalleryData;
  styles?: string;
  context?: unknown;
  variant?: string;
}

// Shared lightbox overlay — reused by carousel-only, stack, and default variants.
//
// Mounted via React portal to document.body so the `position: fixed`
// modal escapes every parent's containing block. Without the portal,
// an ancestor of the gallery card with a `transform` / `filter` /
// `backdrop-filter` / `contain` / `will-change` declaration creates a
// containing block for `position: fixed` descendants, sizing the modal
// against the gallery card (a few hundred pixels wide) instead of the
// viewport. Symptom was "backdrop blurs but no image" — the image WAS
// rendering, just clamped to ~5x5px in the card's coordinate space.
// The portal sidesteps the ancestor-CB question entirely.
//
// Background `--color-product-card-bg` now uses a FLAT hex fallback
// (#ffffff) to avoid the nested var() trap from project memory; the
// previous 3-level chain would silently fall through to the last hex
// if any intermediate var was undefined.
const LightboxOverlay: React.FC<{ src: string; alt: string; originalSrc: string; onClose: () => void }> = ({ src, alt, originalSrc, onClose }) => {
  // SSR guard — createPortal requires a real DOM target. During Astro's
  // SSR pass the overlay never opens (state lives on the client), but
  // belt-and-braces for any consumer that might render this on the
  // server with lightboxOpen=true.
  if (typeof document === 'undefined') return null;
  const canOpenOriginal = /^https?:\/\//i.test(originalSrc) || originalSrc.startsWith('/');

  // Don't render an <img> when src is empty — placeholder/missing image
  // previously left a broken <img> inside the modal which presented as
  // "modal with nothing in it".
  const overlay = (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Product image, fullscreen"
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--style-overlay-bg, rgba(0,0,0,0.85))',
        backdropFilter: 'var(--style-overlay-backdrop, blur(4px))',
        WebkitBackdropFilter: 'var(--style-overlay-backdrop, blur(4px))',
        cursor: 'zoom-out', padding: '24px',
        animation: 'pdp-lightbox-fade-in 0.18s ease-out',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: `@keyframes pdp-lightbox-fade-in { from { opacity: 0 } to { opacity: 1 } }` }} />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative', maxWidth: '95vw', maxHeight: '95vh',
          backgroundColor: 'var(--color-product-card-bg, #ffffff)',
          borderRadius: 'var(--style-modal-radius, 12px)',
          boxShadow: 'var(--style-modal-shadow, 0 30px 80px rgba(0,0,0,0.6))',
          overflow: 'hidden', cursor: 'default',
        }}
      >
        {src && (
          <img
            src={src}
            alt={alt}
            style={{ display: 'block', width: 'auto', maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain' }}
            onError={webpFallback(originalSrc)}
          />
        )}
        {canOpenOriginal && (
          <a
            href={originalSrc}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open full resolution product image in a new tab"
            style={{
              position: 'absolute', bottom: '12px', left: '12px',
              padding: '9px 12px', borderRadius: '8px',
              background: 'rgba(0,0,0,0.8)', color: '#fff',
              fontSize: '14px', fontWeight: 600, textDecoration: 'none',
            }}
          >
            Open full image
          </a>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Close fullscreen image"
          style={{
            position: 'absolute', top: '12px', right: '12px',
            width: '40px', height: '40px', borderRadius: '9999px',
            background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none',
            fontSize: '24px', lineHeight: '1', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          ×
        </button>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export const ProductGallery: React.FC<ProductGalleryProps> = ({ data, styles, context, variant }) => {
  const { product, loading, error } = useProduct(context);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Defensive sweep: the static LCP overlay <img> was REMOVED from
  // ProductPage.astro (2026-07-13 — see the comment there for why), but
  // prebuilt/edge-cached HTML generated before that deploy may still carry
  // one. Removing it on mount is now safe precisely because it is no longer
  // the intended LCP candidate — the SSR'd gallery <img> below is.
  React.useEffect(() => {
    document.querySelectorAll('[data-lcp-static="true"]').forEach((el) => {
      try { el.remove(); } catch { /* noop */ }
    });
  }, []);

  // Esc-to-close + body-scroll-lock for lightbox. Listener only attaches
  // when lightbox is open — zero cost when closed.
  React.useEffect(() => {
    if (!lightboxOpen) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightboxOpen(false); };
    document.addEventListener('keydown', handleEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = prev;
    };
  }, [lightboxOpen]);

  // Settings from preset (merged into data.content, or nested in data.settings for JSON files)
  const glowIntensity: number = data?.glowIntensity ?? data?.settings?.glowIntensity ?? 0.15;
  // additions: thumbnailPosition + zoomMode
  // thumbnailPosition: 'below' (default) | 'side-left' | 'side-right' | 'grid' | 'none'
  const thumbnailPosition: string = data?.content?.thumbnailPosition ?? data?.thumbnailPosition ?? data?.settings?.thumbnailPosition ?? 'below';
  // zoomMode: 'lightbox' (default) | 'inline' | 'off' — wired via data attribute below for CSS / future hook
  const zoomMode: string = data?.content?.zoomMode ?? data?.zoomMode ?? data?.settings?.zoomMode ?? 'lightbox';

  // Layout variant: thumbs-bottom (default) | thumbs-left | carousel-only | stack
  const variantRaw = data?.content?.variant ?? data?.variant ?? 'thumbs-bottom';
  type GalleryLayout = 'thumbs-bottom' | 'thumbs-left' | 'carousel-only' | 'stack';
  const layoutVariant: GalleryLayout =
    (['thumbs-bottom', 'thumbs-left', 'carousel-only', 'stack'] as const).includes(variantRaw as GalleryLayout)
      ? variantRaw as GalleryLayout
      : 'thumbs-bottom';

  // Variant CSS
  const variantClasses = variant === 'bento-dark'
    ? 'bento-card-glow'
    : '';

  if (loading) {
    return (
      <div className={`animate-pulse w-full h-[700px] bg-[var(--surface-2)] ${variantClasses} ${styles ?? ''}`}
        style={{ borderRadius: 'var(--style-card-radius, 16px)' }} />
    );
  }

  if (error || !product) {
    return (
      <div className={`${variantClasses} ${styles ?? ''}`}>
        No Product Images found
      </div>
    );
  }

  // : Token/JSON-provided images take priority
  let mediaGallery: Array<{ url: string; label?: string }>;

  if (data?.images && Array.isArray(data.images) && data.images.length > 0) {
    mediaGallery = data.images;
  } else {
    mediaGallery = (product.media_gallery && product.media_gallery.length > 0)
      ? product.media_gallery
      : (product.image ? [product.image] : []);
  }

  // Empty string when no media — LightboxOverlay guards `src && <img>` so
  // the modal still opens but doesn't render a broken <img>. Previously
  // a placehold.co URL was wired in; if that host was blocked / unreachable,
  // the modal looked empty even though it was technically rendering.
  const activeImage = mediaGallery[activeIndex]?.url || '';
  const altText = mediaGallery[activeIndex]?.label || product.name || 'Product Image';

  // Discount badge for the main PDP image — solid red + white, above the image.
  const galPrice = (product as { price_range?: { minimum_price?: { regular_price?: { value?: number }; final_price?: { value?: number } } } })?.price_range?.minimum_price;
  const galRegular = galPrice?.regular_price?.value;
  const galFinal = galPrice?.final_price?.value;
  const galHasDiscount = galRegular != null && galFinal != null && galFinal < galRegular;
  const galDiscountPct = galHasDiscount ? Math.round((1 - (galFinal / galRegular)) * 100) : 0;
  // LabelsPro badge overlays (designed signals from the Magento module).
  // When present they replace the built-in discount badge — the module's
  // on-sale badge is the designed version of the same signal.
  const labelsProBadges = (product as unknown as {
    extensions?: { labels_pro?: { badges?: import('../../labels/ProductBadge').LabelsProBadgeData[] } };
  })?.extensions?.labels_pro?.badges ?? [];
  const hasImageLabels = labelsProBadges.some(b => (b.placement ?? '').startsWith('image-'));
  const labelsOverlays = hasImageLabels ? <BadgeOverlays badges={labelsProBadges} className="z-20" /> : null;

  const discountBadge = hasImageLabels ? labelsOverlays : (galHasDiscount ? (
    <span
      className="absolute top-3 left-3 z-20 text-xs font-bold px-2.5 py-1"
      style={{ backgroundColor: 'var(--color-discount-badge-bg-solid, #dc2626)', color: 'var(--color-discount-badge-fg, #ffffff)', borderRadius: 'var(--style-badge-radius, 999px)' }}
    >
      -{galDiscountPct}%
    </span>
  ) : null);

  // Show thumbnails when explicitly true OR when thumbnailPosition isn't 'none'
  const showThumbnails = (data?.showThumbnails ?? data?.content?.showThumbnails ?? true) && thumbnailPosition !== 'none';

  // Dynamic glow style on hover — uses brand accent color (--accent-primary-rgb)
  const containerStyle: React.CSSProperties = {
    borderRadius: 'var(--style-card-radius, 16px)',
    overflow: 'hidden',
    transition: 'box-shadow 0.4s ease',
    boxShadow: isHovered
      ? `0 0 40px rgba(var(--accent-primary-rgb, 255, 107, 0), ${glowIntensity}), 0 0 80px rgba(var(--accent-primary-rgb, 255, 107, 0), ${glowIntensity * 0.5})`
      : 'none',
  };

  // Variant: carousel-only — swipe between full images with dot pills, no thumbs
  if (layoutVariant === 'carousel-only') {
    const prev = () => setActiveIndex(i => (i - 1 + mediaGallery.length) % mediaGallery.length);
    const next = () => setActiveIndex(i => (i + 1) % mediaGallery.length);
    return (
      <div
        className={`product-gallery product-gallery--carousel-only relative w-full ${styles ?? ''}`}
        data-variant="carousel-only"
        style={{ ...containerStyle, backgroundColor: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative w-full" style={{ aspectRatio: '1 / 1', backgroundColor: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))' }}>
          {discountBadge}
          {/* LCP: 600px to MATCH the <head> preload + the static LCP <img> in
              ProductPage.astro (both 600). This variant previously rendered 800,
              so on hydration React removed the painted 600 and pulled a fresh,
              un-preloaded 800 — that late paint became the mobile LCP. At 600 the
              first image is a cache HIT (same URL as the preload), so the swap is
              instant and the LCP stays at the early paint. decoding=sync is
              deliberate (paint on the first frame) and is fine on a cached image. */}
          <img
            src={getOptimizedImageUrl(activeImage, 600)}
            alt={altText}
            onError={webpFallback(activeImage)}
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            className="w-full h-full object-contain cursor-zoom-in"
            onClick={() => setLightboxOpen(true)}
            role="button"
            tabIndex={0}
            aria-label={`Open ${altText} in fullscreen`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightboxOpen(true); } }}
          />
          {mediaGallery.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous image"
                className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid var(--glass-border)', color: 'var(--text-primary)' }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
              </button>
              <button
                onClick={next}
                aria-label="Next image"
                className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid var(--glass-border)', color: 'var(--text-primary)' }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
              </button>
              <div
                className="absolute left-1/2 -translate-x-1/2 flex gap-2 px-3 py-1.5"
                style={{
                  bottom: 16,
                  borderRadius: 'var(--style-pill-radius, 999px)',
                  background: 'rgba(0,0,0,0.3)',
                  backdropFilter: 'var(--style-backdrop-filter, blur(6px))',
                  WebkitBackdropFilter: 'var(--style-backdrop-filter, blur(6px))',
                }}
              >
                {mediaGallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    aria-label={`Go to image ${i + 1}`}
                    style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: i === activeIndex ? '#fff' : 'rgba(255,255,255,0.45)',
                      border: 'none', padding: 0, cursor: 'pointer',
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        {lightboxOpen && (
          <LightboxOverlay
            src={getOptimizedImageUrl(activeImage, 1200)}
            alt={altText}
            originalSrc={activeImage}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </div>
    );
  }

  // Variant: stack — full-width main image + horizontally-scrolling thumb strip below
  if (layoutVariant === 'stack') {
    return (
      <div
        className={`product-gallery product-gallery--stack flex flex-col w-full ${styles ?? ''}`}
        data-variant="stack"
        style={{ ...containerStyle, backgroundColor: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="w-full relative"
          style={{ aspectRatio: '16 / 10', backgroundColor: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))' }}
        >
          {discountBadge}
          <img
            src={getOptimizedImageUrl(activeImage, 1200)}
            alt={altText}
            onError={webpFallback(activeImage)}
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            className="w-full h-full object-contain cursor-zoom-in"
            onClick={() => setLightboxOpen(true)}
            role="button"
            tabIndex={0}
            aria-label={`Open ${altText} in fullscreen`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightboxOpen(true); } }}
          />
        </div>
        {mediaGallery.length > 1 && (
          <div
            className="flex gap-3 overflow-x-auto p-3 no-scrollbar"
            style={{ background: 'var(--surface-1)', borderTop: '1px solid var(--glass-border)' }}
          >
            {mediaGallery.map((media, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`flex-shrink-0 border ${activeIndex === idx ? 'border-[var(--accent-primary)]' : 'border-[var(--glass-border)]'} rounded p-1 hover:border-[var(--color-global-link-hover)] transition-colors`}
                style={{ background: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))', width: 88, height: 88 }}
                aria-label={`Show image ${idx + 1}`}
              >
                <img
                  src={getOptimizedImageUrl(media.url, 160)}
                  alt={media.label || product.name}
                  onError={webpFallback(media.url)}
                  className="w-full h-full object-contain"
                  loading={idx < 4 ? 'eager' : 'lazy'}
                  fetchPriority="low"
                />
              </button>
            ))}
          </div>
        )}
        {lightboxOpen && (
          <LightboxOverlay
            src={getOptimizedImageUrl(activeImage, 1200)}
            alt={altText}
            originalSrc={activeImage}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex w-full product-gallery thumbs-${thumbnailPosition} zoom-${zoomMode} ${styles ?? ''}`.replace(/\s+/g, ' ').trim()}
      data-thumb-position={thumbnailPosition}
      data-zoom-mode={zoomMode}
      style={{
        ...containerStyle,
        backgroundColor: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))',
        flexDirection: thumbnailPosition === 'below' || thumbnailPosition === 'grid' ? 'column' : 'row',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showThumbnails !== false && (
        /* Fix the rail and every thumb to explicit dimensions before images
           load. A tall screenshot can otherwise impose its intrinsic height
           on an aspect-square thumb, crowding neighbouring thumbnails on
           narrow screens and shifting the main image. */
        <div className="w-20 border-r border-slate-200 flex flex-col gap-4 p-4 no-scrollbar overflow-y-auto bg-[var(--surface-1)]" style={{ width: '5rem', flexShrink: 0 }}>
          {mediaGallery.map((media: GalleryImage, idx: number) => (
            <button
              key={idx}
              type="button"
              className={`block border ${activeIndex === idx ? 'border-[var(--accent-primary)]' : 'border-[var(--glass-border)]'} rounded p-1 cursor-pointer hover:border-[var(--color-global-link-hover)] transition-colors`}
              style={{ backgroundColor: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))', width: 48, height: 48, minHeight: 48, flexShrink: 0, overflow: 'hidden' }}
              onClick={() => setActiveIndex(idx)}
              aria-label={`Show image ${idx + 1} of ${mediaGallery.length}`}
              aria-current={activeIndex === idx ? 'true' : undefined}
          >
            <img
              alt={media.label || product.name}
              className="block object-contain"
              style={{ width: 38, height: 38 }}
              src={getOptimizedImageUrl(media.url, 130)}
              onError={webpFallback(media.url)}
              width={38}
              height={38}
              /* first 3 thumbs eager. Gallery
                 thumbnails are above-fold on PDP (visible alongside the
                 main image). Lazy-loading them caused a small but
                 measurable delay before they appeared after layout
                 settled. Beyond the 3rd, lazy is fine —
                 those are below-fold or off-screen on most viewports.
                 fetchPriority=low keeps the eager ones from contending
                 with the preloaded LCP hero for bandwidth: the R2 width
                 floor snaps these to the 320 rendition (~9-13 KiB each),
                 so three of them share the LCP window otherwise. */
              loading={idx < 3 ? "eager" : "lazy"}
              fetchPriority="low"
            />
            </button>
          ))}
        </div>
      )}
      {/* CLS: reserve a fixed 1/1 box (capped 600px) BEFORE the image paints,
          matching the 'carousel' and 'stack' variants above which already do
          this via an aspectRatio container. The old `flex-1 … h-auto` reserved
          no row height until the image's intrinsic ratio resolved, so under
          throttled mobile the grid-pcr-hero row jumped on paint (Lighthouse
          CLS 0.103). Square matches the image's 600x600 attrs, so no visual
          change — the row just holds its height from first paint. */}
      <div className="relative flex flex-col items-center justify-center overflow-hidden w-full" style={{ aspectRatio: '1 / 1', maxHeight: '600px', borderRadius: 'var(--style-img-radius, 16px)', backgroundColor: 'var(--color-product-card-bg, var(--style-img-bg, #ffffff))' }}>
        {discountBadge}
        {/* Click-to-zoom: opens a fullscreen lightbox with an HD (1200px)
            image. The HD image fetch is deferred until the user clicks —
            zero impact on initial page load. The
            modal markup mounts conditionally, so when closed there is
            literally no DOM cost. */}
        <img
          className="w-full h-full object-contain cursor-zoom-in"
          alt={altText}
          src={getOptimizedImageUrl(activeImage, 600)}
          onError={webpFallback(activeImage)}
          width={600}
          height={600}
          fetchPriority="high"
          /* paint hints for LCP. loading=eager
             skips the lazy-load heuristic browsers normally apply, and
             decoding=sync forces the IMG to be decoded inline with the
             paint pass instead of off-thread. Both push the IMG onto
             the first paint frame instead of a later frame. */
          loading="eager"
          decoding="sync"
          onClick={() => setLightboxOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightboxOpen(true); } }}
          aria-label={`Open ${altText} in fullscreen`}
        />
        {/* Visible enlarge-icon hint so users know the image is clickable.
            Stops propagation isn't required because the click action is the
            same (open lightbox). Pure visual affordance — `cursor-zoom-in`
            alone wasn't communicating to users that the image expands. */}
        {zoomMode !== 'off' && (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label={`Open ${altText} in fullscreen`}
            title="Click to enlarge"
            className="absolute bottom-3 right-3 w-10 h-10 flex items-center justify-center transition-colors"
            style={{
              borderRadius: 'var(--style-btn-radius, 999px)',
              background: 'color-mix(in srgb, var(--surface-1) 80%, transparent)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        )}
      </div>

      {/* Lightbox — uses the shared portal-mounted LightboxOverlay so all
          variants benefit from the containing-block escape (see comment on
          LightboxOverlay at top of file). Was previously a duplicate inline
          modal that occupied lines 470-541; consolidated here. */}
      {lightboxOpen && (
        <LightboxOverlay
          src={getOptimizedImageUrl(activeImage, 1200)}
          alt={altText}
          originalSrc={activeImage}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default ProductGallery;
